import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LiveTournamentList from '../components/LiveTournamentList';
import TournamentList from '../components/TournamentList';
import CreateLeagueModal from '../components/CreateLeagueModal';
import LoginModal from '../components/LoginModal';
import JoinPublicLeagueModal from '../components/JoinPublicLeagueModal';
import JoinLeagueModal from '../components/JoinLeagueModal';
import AllLeaguesModal from '../components/AllLeaguesModal';
import { useAuth } from '../hooks/AuthContext';
import { apiService } from '../services/api';

const Tournaments: React.FC = () => {
  const { user, isAuthenticated, login, isLoading } = useAuth();
  const navigate = useNavigate();
  const [selectedLiveTournament, setSelectedLiveTournament] = useState<any | null>(null);
  const [fantasyLeagues, setFantasyLeagues] = useState<any[]>([]);
  const [loadingLeagues, setLoadingLeagues] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateLeagueModal, setShowCreateLeagueModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showPublicJoinModal, setShowPublicJoinModal] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState<any | null>(null);
  const [showAllLeaguesModal, setShowAllLeaguesModal] = useState(false);
  const [allLeagues, setAllLeagues] = useState<any[]>([]);
  const [loadingAllLeagues, setLoadingAllLeagues] = useState(false);

  const handleSelectLiveTournament = async (tournament: any) => {
    setSelectedLiveTournament(tournament);
    setLoadingLeagues(true);
    setError(null);
    
    try {
      const data = await apiService.getFantasyLeaguesByTournament(tournament.id);
      
      if (data.success) {
        setFantasyLeagues(data.fantasyLeagues);
      } else {
        setError('Failed to load fantasy leagues');
      }
    } catch (err) {
      setError('Failed to load fantasy leagues');
      console.error('Error fetching fantasy leagues:', err);
    } finally {
      setLoadingLeagues(false);
    }
  };

  const handleBack = () => {
    setSelectedLiveTournament(null);
    setFantasyLeagues([]);
    setError(null);
  };

  const handleCreateLeague = () => {
    console.log('🔍 Create League clicked');
    console.log('🔍 Current auth state - isAuthenticated:', isAuthenticated);
    console.log('🔍 Current auth state - user:', user);
    console.log('🔍 Token in localStorage:', localStorage.getItem('authToken') ? 'exists' : 'missing');
    console.log('🔍 useAuth hook state:', { user, isAuthenticated, isLoading });
    
    // Wait for authentication to finish loading
    if (isLoading) {
      console.log('🔍 Authentication still loading, please wait...');
      return;
    }
    
    if (!isAuthenticated) {
      console.log('🔍 User not authenticated, showing login modal');
      setShowLoginModal(true);
      return;
    }
    
    // Show the create league modal for any tournament selection
    console.log('🔍 Showing create league modal');
    setShowCreateLeagueModal(true);
  };

  const handleLeagueCreated = () => {
    // Refresh the leagues list
    if (selectedLiveTournament) {
      handleSelectLiveTournament(selectedLiveTournament);
    }
  };

  const handleJoinLeague = (league: any) => {
    setSelectedLeague(league);
    
    if (league.is_public) {
      setShowPublicJoinModal(true);
    } else {
      setShowJoinModal(true);
    }
  };

  const handleJoinSuccess = () => {
    // Refresh the leagues list
    if (selectedLiveTournament) {
      handleSelectLiveTournament(selectedLiveTournament);
    }
    // Also refresh all leagues if the modal is open
    if (showAllLeaguesModal) {
      handleShowAllLeagues();
    }
  };

  const handleShowAllLeagues = async () => {
    setShowAllLeaguesModal(true);
    setLoadingAllLeagues(true);
    setError(null);
    
    try {
      const data = await apiService.getFantasyLeagues();
      
      if (data.success) {
        // Filter to show only public leagues or leagues the user can join
        const joinableLeagues = data.tournaments.filter((league: any) => 
          league.is_public || league.tournament_type === 'pickem' || league.type === 'pickem'
        );
        setAllLeagues(joinableLeagues);
      } else {
        setError('Failed to load leagues');
      }
    } catch (err) {
      setError('Failed to load leagues');
      console.error('Error fetching all leagues:', err);
    } finally {
      setLoadingAllLeagues(false);
    }
  };



  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    // After successful login, navigate to create league page
    navigate('/create-league');
  };

  const handleLogin = async (email: string, password: string) => {
    try {
      await login(email, password);
      // Add a small delay to ensure authentication state is updated
      await new Promise(resolve => setTimeout(resolve, 100));
      console.log('🔍 After login - checking auth state again');
      console.log('🔍 Token in localStorage:', localStorage.getItem('authToken') ? 'exists' : 'missing');
      // After successful login, navigate to create league page
      setShowLoginModal(false);
      navigate('/create-league');
    } catch (error) {
      // Error is handled by the LoginModal component
      throw error;
    }
  };

  const handleSwitchToRegister = () => {
    // For now, just close login modal and show a message
    setShowLoginModal(false);
    alert('Please register first. You can do this from the main navigation.');
  };

  // Helper functions for league display
  const getLeagueTypeInfo = (type: string) => {
    switch (type) {
      case 'pickem':
        return { label: 'Pick\'em', icon: '🎯', color: 'bg-purple-600' };
      case 'budget_league':
        return { label: 'Budget League', icon: '💰', color: 'bg-green-600' };
      case 'draft_league':
        return { label: 'Draft League', icon: '📋', color: 'bg-blue-600' };
      case 'h2h':
        return { label: 'H2H', icon: '⚔️', color: 'bg-red-600' };
      case 'eliminator':
        return { label: 'Eliminator', icon: '🏆', color: 'bg-yellow-600' };
      default:
        return { label: type, icon: '🏏', color: 'bg-gray-600' };
    }
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'upcoming':
        return { label: 'Upcoming', color: 'bg-blue-600 text-white' };
      case 'active':
        return { label: 'Active', color: 'bg-green-600 text-white' };
      case 'completed':
        return { label: 'Completed', color: 'bg-gray-600 text-white' };
      default:
        return { label: status, color: 'bg-gray-600 text-white' };
    }
  };



  return (
    <div className="max-w-4xl mx-auto py-8 px-2">
      {!selectedLiveTournament ? (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">Live Tournaments</h2>
            <div className="flex flex-col sm:flex-row gap-2 sm:space-x-3">
              <button
                onClick={() => setShowJoinModal(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-sm sm:text-base font-medium transition-colors"
              >
                Join Private League
              </button>
              <button
                onClick={handleCreateLeague}
                disabled={isLoading}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-sm sm:text-base font-medium transition-colors ${
                  isLoading 
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {isLoading ? 'Loading...' : 'Create League'}
              </button>
            </div>
          </div>
          
          <LiveTournamentList onSelect={handleSelectLiveTournament} isAdmin={!!user?.is_admin} />
        </div>
      ) : (
        <div>
          <button
            onClick={handleBack}
            className="mb-4 bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            ← Back to Live Tournaments
          </button>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-white">
              Fantasy Leagues for {selectedLiveTournament.name} ({selectedLiveTournament.format} {selectedLiveTournament.year})
            </h2>
            <button
              onClick={handleCreateLeague}
              disabled={isLoading}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isLoading 
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {isLoading ? 'Loading...' : 'Create League'}
            </button>
          </div>
          {loadingLeagues ? (
            <div className="text-white py-8">Loading fantasy leagues...</div>
          ) : error ? (
            <div className="text-red-400 py-8">{error}</div>
          ) : (
            <TournamentList 
              tournaments={fantasyLeagues} 
              onJoinLeague={handleJoinLeague}
              showJoinButton={true}
            />
          )}
        </div>
      )}

      {/* Create League Modal */}
      <CreateLeagueModal
        isOpen={showCreateLeagueModal}
        onClose={() => setShowCreateLeagueModal(false)}
        tournamentId={selectedLiveTournament?.id}
        tournamentName={selectedLiveTournament?.name}
        onLeagueCreated={handleLeagueCreated}
      />

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={handleLogin}
        onSwitchToRegister={handleSwitchToRegister}
      />

      {/* Join League Modal for Private Leagues */}
      <JoinLeagueModal
        isOpen={showJoinModal}
        onClose={() => {
          setShowJoinModal(false);
          setSelectedLeague(null);
        }}
        onSuccess={handleJoinSuccess}
        leagueId={selectedLeague?.id}
        isPublicLeague={false}
        leagueType={selectedLeague?.league_type}
      />

      {/* Join Public League Modal */}
      {selectedLeague && (
        <JoinPublicLeagueModal
          isOpen={showPublicJoinModal}
          onClose={() => {
            setShowPublicJoinModal(false);
            setSelectedLeague(null);
          }}
          onSuccess={handleJoinSuccess}
          leagueId={selectedLeague.id}
          leagueName={selectedLeague.name}
          currentParticipants={selectedLeague.current_participants}
          maxParticipants={selectedLeague.max_participants}
        />
      )}

      {/* All Leagues Modal */}
      <AllLeaguesModal
        isOpen={showAllLeaguesModal}
        onClose={() => setShowAllLeaguesModal(false)}
        onJoinLeague={handleJoinLeague}
        leagues={allLeagues}
        loading={loadingAllLeagues}
        error={error}
      />
    </div>
  );
};

export default Tournaments;
